import { useActiveAssignment } from "@/store/selectors/assignmentSelectors";
import { useAssignmentPlaylists } from "@/store/selectors/assignmentSelectors";
import { useActiveUser, useAdvizerMap } from "@/store/selectors";
import { useCallback, useMemo } from "react";
import { AssignmentService } from "@/services/AssignmentService";
import { PlaylistService } from "@/services/PlaylistService";
import { Space, Stack, Group, Checkbox, Button } from "@mantine/core";
import { PlaylistCard } from "../playlists/PlaylistCard";
import { PlaylistCheckbox } from "./PlaylistCheckbox";
import { AdvizerCheckbox } from "./AdvizerCheckbox";
import { usePlaylistLibrary } from "@/store/selectors/playlistSelectors";
import { Text } from "../ui/Text";
import { IAssignmentPrompt } from "@/types/AssignmentTypes";
import { Textarea } from "../ui/Textarea";
import { TotalTimeCheckbox } from "./TotalTimeCheckbox";
import { TotalRequiredCountCheckbox } from "./TotalRequiredCountCheckbox";
import { PDFRecsCheckbox } from "./PDFRecsCheckbox";
import { config } from "@/config";

export const AssignmentChecklistItems = ({
  userVideoData,
  userVideosWatched,
  isAssignmentResultItem = false,
  userPlaylistData,
  userResponses,
  isExportView,
  advizersOnly,
}: {
  userVideoData: any;
  userVideosWatched: any[];
  isAssignmentResultItem?: boolean;
  userPlaylistData: any;
  userResponses: any;
  isExportView?: boolean;
  advizersOnly?: boolean;
}) => {
  const activeAssignment = useActiveAssignment();
  const assignmentPlaylists = useAssignmentPlaylists();
  const advizerMap = useAdvizerMap();
  const playlistLibrary = usePlaylistLibrary();

  const didGetRecsFromUpload = useMemo(() => {
    return !!userResponses?.careerLeader?.response;
  }, [userResponses]);

  const rankedAdvizersWatchedData = useMemo(() => {
    return AssignmentService.getUserRankedAdvizersWatchedData({
      advizerMap,
      userVideosWatched,
      userVideoData,
    });
  }, [advizerMap, userVideosWatched, userVideoData]);

  const rankedPlaylistsWatchedData = useMemo(() => {
    return AssignmentService.getUserRankedPlaylistsWatchedData({
      userPlaylistData,
      playlistLibrary,
    });
  }, [userPlaylistData, playlistLibrary]);

  const hasMinutesToWatchTarget = useMemo(() => {
    return (
      activeAssignment?.totalMinutesToComplete &&
      activeAssignment?.assignmentType === "anyAdvizers"
    );
  }, [
    activeAssignment?.assignmentType,
    activeAssignment?.totalMinutesToComplete,
  ]);

  const isCareerLeaderUploadAssignment =
    activeAssignment?.uploadType === "careerLeader";

  const totalSecondsWatchedByUser = useMemo(() => {
    return rankedAdvizersWatchedData.reduce(
      (acc, advizer) => acc + advizer.totalSecondsWatched,
      0,
    );
  }, [rankedAdvizersWatchedData]);

  const assignmentPrompts = useMemo(() => {
    if (!activeAssignment) return [];
    return AssignmentService.getArrayOfAssignmentPrompts({
      assignment: activeAssignment,
    });
  }, [activeAssignment]);

  const completedPlaylistKeys = useMemo(() => {
    return PlaylistService.getUserCompletedPlaylistKeys({
      userPlaylistData,
    });
  }, [userPlaylistData]);

  const renderPercentChangeInUnderstanding = useCallback(
    (params: {
      playlistData: any;
      isPlaylistCompleted: boolean;
      isAssignmentResultItem: boolean;
    }) => {
      const { playlistData, isPlaylistCompleted } = params;

      if (!playlistData || !isPlaylistCompleted) return null;

      return (
        <Stack gap={0} align="center" pl="md">
          <Text size="md" titleStyle="h5" lh={1.2} c="primary">
            {(playlistData.percentChangeInUnderstanding || 0) > 0 ? "+" : ""}
            {playlistData.percentChangeInUnderstanding.toFixed(0)}%
          </Text>
          <Text fz={9} ta="center" lh={1.2}>
            understanding
          </Text>
        </Stack>
      );
    },
    [],
  );
  const renderAssignmentPromptCheckboxItems = useCallback(() => {
    const isSmall = isAssignmentResultItem;

    return assignmentPrompts
      .sort((a, b) => {
        return a.createdAt - b.createdAt;
      })
      .map((prompt) => {
        const responseData = userResponses?.[prompt.id] || {};
        const isPromptCompleted =
          AssignmentService.checkDidUserCompleteAssignmentPrompt({
            responseDataForPrompt: responseData,
          });
        return (
          <Stack key={prompt.id} gap={0}>
            <Space h={isAssignmentResultItem ? "md" : "xl"} />
            <AssignmentPromptCheckboxItem
              readOnly={isAssignmentResultItem || isExportView || false}
              noMaxHeight={isExportView}
              prompt={prompt}
              isSmall={isSmall}
              isCompleted={isPromptCompleted}
              responseData={responseData}
            />
          </Stack>
        );
      });
  }, [assignmentPrompts, isAssignmentResultItem, isExportView, userResponses]);

  const renderCoreAssignmentChecklistItems = useCallback(
    (params?: { hideCheckboxes?: boolean }) => {
      const { hideCheckboxes } = params || {};
      if (
        activeAssignment?.assignmentType === "specificPlaylists" &&
        !advizersOnly
      ) {
        return (
          <>
            <Stack gap={isAssignmentResultItem ? "md" : "xl"}>
              {assignmentPlaylists.map((playlist) => {
                const playlistData = rankedPlaylistsWatchedData.find(
                  (playlistData) => playlistData.id === playlist.id,
                );

                const isPlaylistCompleted = completedPlaylistKeys.includes(
                  playlist.id,
                );
                return (
                  <Group key={playlist.id}>
                    <PlaylistCheckbox
                      playlistId={playlist.id}
                      playlistData={playlistData}
                      title={playlist.title || ""}
                      isSmall={isAssignmentResultItem}
                      userVideoData={userVideoData}
                      userPlaylistData={userPlaylistData}
                      isPlaylistCompleted={isPlaylistCompleted}
                    />

                    {isAssignmentResultItem || isPlaylistCompleted ? null : (
                      <Group
                        justify="flex-start"
                        w={120}
                        style={{ overflow: "hidden" }}
                      >
                        <PlaylistCard
                          playlistId={playlist.id}
                          startButtonOnly
                        />
                      </Group>
                    )}

                    {renderPercentChangeInUnderstanding({
                      playlistData,
                      isPlaylistCompleted,
                      isAssignmentResultItem,
                    })}
                  </Group>
                );
              })}
            </Stack>
          </>
        );
      }

      if (
        activeAssignment?.assignmentType === "anyPlaylists" &&
        !advizersOnly
      ) {
        return (
          <>
            <Stack gap={isAssignmentResultItem ? "lg" : "xl"} w="100%">
              {Array.from({
                length: activeAssignment.requiredCount || 1,
              }).map((_, index) => {
                const playlistData = rankedPlaylistsWatchedData[index] || null;
                const isPlaylistCompleted = completedPlaylistKeys.includes(
                  playlistData?.id,
                );

                return (
                  <Group key={index} w="100%" flex={1} justify="space-between">
                    <PlaylistCheckbox
                      playlistId={playlistData?.id}
                      playlistData={playlistData}
                      title={playlistData?.playlist?.title}
                      isSmall={isAssignmentResultItem}
                      userVideoData={userVideoData}
                      userPlaylistData={userPlaylistData}
                      isPlaylistCompleted={isPlaylistCompleted}
                    />

                    {isAssignmentResultItem || isPlaylistCompleted ? null : (
                      <Group
                        justify="flex-start"
                        w={120}
                        style={{ overflow: "hidden" }}
                      >
                        <PlaylistCard
                          playlistId={playlistData?.id || ""}
                          startButtonOnly
                        />
                      </Group>
                    )}

                    {renderPercentChangeInUnderstanding({
                      playlistData,
                      isPlaylistCompleted,
                      isAssignmentResultItem,
                    })}
                  </Group>
                );
              })}
            </Stack>
          </>
        );
      }

      if (activeAssignment?.assignmentType === "anyAdvizers" || advizersOnly) {
        const showAllAdvizers =
          advizersOnly ||
          (isAssignmentResultItem &&
            !!activeAssignment?.totalMinutesToComplete);
        return (
          <>
            {!isAssignmentResultItem ? <Space h="lg" /> : null}
            <Stack gap={isAssignmentResultItem ? "md" : "xl"} w="100%">
              {(showAllAdvizers
                ? rankedAdvizersWatchedData
                : Array.from({
                    length: activeAssignment?.requiredCount || 1,
                  })
              ).map((_, index) => {
                const advizerId = rankedAdvizersWatchedData[index]?.id || null;
                if ((advizersOnly || isAssignmentResultItem) && !advizerId) {
                  return null;
                }
                const isAdvizerCompleted =
                  AssignmentService.checkIsAdvizerCompletedForAssignment({
                    advizerId,
                    rankedAdvizerWatchDataForSingleAdvizer:
                      rankedAdvizersWatchedData[index] || {},
                  });
                return (
                  <AdvizerCheckbox
                    key={index}
                    index={index}
                    advizerId={advizerId}
                    advizerData={rankedAdvizersWatchedData[index]}
                    isAssignmentResultItem={isAssignmentResultItem}
                    isSmall={isAssignmentResultItem}
                    isCompleted={isAdvizerCompleted}
                    isExportView={isExportView}
                    hideCheckbox={advizersOnly || hideCheckboxes}
                  />
                );
              })}
            </Stack>
          </>
        );
      }
      return null;
    },
    [
      activeAssignment?.assignmentType,
      activeAssignment?.requiredCount,
      activeAssignment?.totalMinutesToComplete,
      advizersOnly,
      isAssignmentResultItem,
      assignmentPlaylists,
      rankedPlaylistsWatchedData,
      completedPlaylistKeys,
      userVideoData,
      userPlaylistData,
      renderPercentChangeInUnderstanding,
      rankedAdvizersWatchedData,
      isExportView,
    ],
  );

  const renderTotalTimeChecklistItem = useCallback(() => {
    if (!activeAssignment?.totalMinutesToComplete) return null;
    if (activeAssignment?.assignmentType !== "anyAdvizers") return null;

    const didUserWatchEnoughTime =
      totalSecondsWatchedByUser >=
      (activeAssignment?.totalMinutesToComplete || 0) * 60;

    return (
      <>
        <Space h="sm" />
        <Stack gap={0}>
          <TotalTimeCheckbox
            isSmall={isAssignmentResultItem}
            isExportView={isExportView}
            isCompleted={didUserWatchEnoughTime}
            totalMinutesToComplete={
              activeAssignment?.totalMinutesToComplete || 0
            }
            totalSecondsWatchedByUser={totalSecondsWatchedByUser}
          />
        </Stack>
        <Space h="lg" />
      </>
    );
  }, [
    activeAssignment?.assignmentType,
    activeAssignment?.totalMinutesToComplete,
    isAssignmentResultItem,
    isExportView,
    totalSecondsWatchedByUser,
  ]);

  const renderTotalRequiredCountChecklistItem = useCallback(() => {
    if (
      !activeAssignment?.requiredCount ||
      activeAssignment?.requiredCount === 1
    )
      return null;
    if (activeAssignment?.assignmentType !== "anyAdvizers") return null;

    const didUserWatchEnoughAdvizers =
      rankedAdvizersWatchedData.length >=
      (activeAssignment?.requiredCount || 0);

    return (
      <>
        <Space h="sm" />
        <Stack gap={0}>
          <TotalRequiredCountCheckbox
            isSmall={isAssignmentResultItem}
            isExportView={isExportView}
            isCompleted={didUserWatchEnoughAdvizers}
            totalAdvizersWatched={rankedAdvizersWatchedData.length}
            totalAdvizersRequired={activeAssignment?.requiredCount || 0}
          />
          <Space h="sm" />
        </Stack>
      </>
    );
  }, [
    activeAssignment?.assignmentType,
    activeAssignment?.requiredCount,
    isAssignmentResultItem,
    isExportView,
    rankedAdvizersWatchedData.length,
  ]);

  const renderPDFRecsCheckbox = useCallback(() => {
    if (!isCareerLeaderUploadAssignment) return null;
    return (
      <>
        <Space h="sm" />
        <PDFRecsCheckbox
          isSmall={isAssignmentResultItem}
          isCompleted={didGetRecsFromUpload}
        />
        <Space h="sm" />
      </>
    );
  }, [
    didGetRecsFromUpload,
    isAssignmentResultItem,
    isCareerLeaderUploadAssignment,
  ]);

  if (advizersOnly) {
    return (
      <Stack gap={0} w="100%">
        {renderCoreAssignmentChecklistItems()}
      </Stack>
    );
  }

  return (
    <Stack gap={0} w="100%">
      {renderPDFRecsCheckbox()}
      {renderTotalTimeChecklistItem()}
      {renderTotalRequiredCountChecklistItem()}
      {!hasMinutesToWatchTarget ? renderCoreAssignmentChecklistItems() : null}
      {renderAssignmentPromptCheckboxItems()}
      {hasMinutesToWatchTarget && isAssignmentResultItem ? (
        <>
          <Space h="lg" />
          {renderCoreAssignmentChecklistItems({ hideCheckboxes: true })}
        </>
      ) : null}
    </Stack>
  );
};

function AssignmentPromptCheckboxItem({
  prompt,
  isSmall,
  isCompleted,
  responseData,
  readOnly,
  noMaxHeight,
}: {
  prompt: IAssignmentPrompt;
  isSmall: boolean;
  isCompleted: boolean;
  responseData: any;
  readOnly: boolean;
  noMaxHeight?: boolean;
}) {
  const activeAssignment = useActiveAssignment();
  const activeUser = useActiveUser();

  return (
    <Group
      flex={1}
      gap={0}
      // opacity={isCompleted ? 0.7 : 1}
      align="flex-start"
      w="100%"
    >
      <Group pt={2}>
        <Checkbox
          color="primary"
          size={isSmall ? "xs" : "md"}
          readOnly
          checked={isCompleted}
        />
      </Group>

      <Space w="lg" />

      <Stack w="100%" flex={1} gap="xs">
        <Button unstyled size="xs">
          <Group>
            {/* <Box
              w={40}
              h={40}
              bg="primary.0"
              style={{
                borderRadius: 20,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              display="flex"
            >
              <IconWriting size={20} color={theme.colors.primary[7]} />
            </Box> */}
            <Text
              size="md"
              titleStyle={isSmall ? "h6" : "h5"}
              fw={500}
              ta="left"
            >
              {prompt.prompt}
            </Text>
          </Group>
        </Button>

        {responseData.response || !isSmall ? (
          <Textarea
            withEditor
            readOnly={readOnly}
            noMaxHeight={noMaxHeight}
            id={`assignment-prompt-${prompt.id}`}
            title={prompt.prompt}
            label={prompt.prompt}
            minCharacters={config.minimumReflectionResponseCharacterLength}
            onDebouncedChange={(value) => {
              if (!activeAssignment?.id || !activeUser?.id || !prompt.id) {
                return;
              }

              AssignmentService.updateUserAssignmentResponse({
                assignmentId: activeAssignment?.id,
                studentUserId: activeUser?.id,
                promptId: prompt.id,
                response: value,
              });
            }}
            value={responseData?.response}
            placeholder="Write your response here..."
            styles={{
              input: { backgroundColor: "transparent", paddingBottom: 40 },
            }}
          />
        ) : (
          <Text c="dimmed" size="xs" pl={2}>
            No response yet.
          </Text>
        )}
      </Stack>
    </Group>
  );
}
